import React from 'react';
import {
  PieChart,
  Box,
  SpaceBetween,
  Container,
  Header,
  Button,
  ColumnLayout,
} from '@amzn/awsui-components-react/polaris';
import { CHART_COLORS } from './constants';

export const DeploymentCharts = ({
                                   stackName,
                                   releaseVersion,
                                   metricsData,
                                   selectedStatus,
                                   setSelectedStatus
                                 }) => {
  const handleStatusSelect = (status) => {
    setSelectedStatus(prev => prev === status ? null : status);
  };

  const prepareChartData = () => {
    if (!metricsData?.externalMetrics) return [];

    return metricsData.externalMetrics
      .map(metric => {
        const lastValue = metric.values[0] || 0;
        const status = metric.label.split('.').pop();

        return {
          title: status,
          value: lastValue,
          color: CHART_COLORS[status] || CHART_COLORS.IN_PROGRESS
        };
      })
      .filter(item => item.value > 0);
  };

  const getAllStatusCounts = () => {
    if (!metricsData?.externalMetrics) return {};

    const counts = metricsData.externalMetrics.reduce((acc, metric) => {
      const status = metric.label.split('.').pop();
      acc[status] = metric.values[0] || 0;
      return acc;
    }, {});

    Object.keys(CHART_COLORS).forEach(status => {
      if (!(status in counts)) {
        counts[status] = 0;
      }
    });

    return counts;
  };

  const pieData = prepareChartData();
  const totalClusters = pieData.reduce((sum, item) => sum + item.value, 0);
  const allStatusCounts = getAllStatusCounts();

  const renderStatusCounts = () => {
    const statusEntries = Object.entries(allStatusCounts);
    const STATUS_GRID_COLUMNS = 2;
    const itemsPerColumn = Math.ceil(statusEntries.length / STATUS_GRID_COLUMNS);

    return (
      <Box>
        <ColumnLayout columns={STATUS_GRID_COLUMNS} variant="text-grid">
          {[...Array(STATUS_GRID_COLUMNS)].map((_, columnIndex) => (
            <SpaceBetween size="s" direction="vertical" key={columnIndex}>
              {statusEntries
                .slice(columnIndex * itemsPerColumn, (columnIndex + 1) * itemsPerColumn)
                .map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: CHART_COLORS[status] || CHART_COLORS.IN_PROGRESS,
                        marginRight: '6px',
                        borderRadius: '2px',
                        display: 'inline-block'
                      }}
                    />
                    <span><strong>{status}:</strong> {count}</span>
                  </div>
                ))}
            </SpaceBetween>
          ))}
        </ColumnLayout>
      </Box>
    );
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header>Release Deployment Status: {releaseVersion}</Header>
        <Box padding="l">
          <SpaceBetween size="m">
            <Box textAlign="center">
              <Header variant="h3">
                Total Clusters: {totalClusters}
              </Header>
            </Box>
            {pieData.length > 0 && (
              <PieChart
                data={pieData}
                detailPopoverContent={(datum, sum) => [
                  { key: "Status", value: datum.title },
                  { key: "Clusters", value: datum.value },
                  { key: "Percentage", value: `${((datum.value / sum) * 100).toFixed(1)}%` },
                  {
                    key: "Action",
                    value: (
                      <Button
                        variant="inline-link"
                        onClick={() => handleStatusSelect(datum.title)}
                      >
                        {selectedStatus === datum.title ? 'Hide details' : 'View details'}
                      </Button>
                    )
                  }
                ]}
                size="medium"
                variant="donut"
                title="Cluster Status Distribution"
                hideTitles={false}
                hideFilter={true}
                hideLegend={true}
                onSegmentClick={({ detail }) => handleStatusSelect(detail.datum.title)}
              />
            )}
            {renderStatusCounts()}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
};